import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { verifyEvent } from 'nostr-tools';
import {
  PINNED_NOTE_IDS,
  PINNED_FALLBACKS,
  PINNED_LOOKUP_AUTHOR,
} from '@/lib/pinnedNotes';
import { PINNED_LOOKUP_RELAYS } from '@/lib/appRelays';

/** Ids that resolved via an unverified reconstruction (for UI marking). */
export const unverifiedPinnedIds = new Set<string>();

/**
 * Build the set of pinned notes from the embedded fallbacks alone (no relays).
 * Used as synchronous initialData so pinned notes render INSTANTLY — the relay
 * query then runs in the background and upgrades unverified reconstructions to
 * real signed copies if any relay actually serves them.
 */
function buildFallbackNotes(): NostrEvent[] {
  const byId = new Map<string, NostrEvent>();
  for (const fb of PINNED_FALLBACKS) {
    const ev = fb.event;
    if (fb.verified) {
      try {
        if (verifyEvent(ev)) byId.set(ev.id, ev);
        else console.warn('[pinned] verified fallback failed signature', ev.id);
      } catch {
        /* ignore */
      }
    } else if (ev.pubkey === PINNED_LOOKUP_AUTHOR) {
      byId.set(ev.id, ev);
      unverifiedPinnedIds.add(ev.id);
    }
  }
  return Array.from(byId.values());
}

const FALLBACK_NOTES = buildFallbackNotes();

/** The built-in copy of a pinned note, or null if this id isn't one of them. */
export function findPinnedFallback(id: string): NostrEvent | null {
  return FALLBACK_NOTES.find((ev) => ev.id === id) ?? null;
}

/**
 * Curated "must-have" notes.
 *
 * Renders embedded fallbacks immediately (initialData), then queries a broad
 * deep-history relay GROUP in the background. If a relay returns the real
 * signed event, it replaces the reconstruction and clears the "unverified"
 * flag. If no relay has it, the embedded copy simply stays.
 */
export function usePinnedNotes() {
  const { nostr } = useNostr();

  return useQuery<NostrEvent[]>({
    queryKey: ['archive', 'pinned', PINNED_NOTE_IDS],
    initialData: FALLBACK_NOTES,
    queryFn: async ({ signal }) => {
      const byId = new Map<string, NostrEvent>();

      // 1) Relay lookup — authoritative, replaces reconstructions.
      if (PINNED_NOTE_IDS.length > 0) {
        try {
          const group = nostr.group(PINNED_LOOKUP_RELAYS);
          const events = await group.query(
            [
              { ids: PINNED_NOTE_IDS },
              { ids: PINNED_NOTE_IDS, authors: [PINNED_LOOKUP_AUTHOR], kinds: [1] },
            ],
            {
              signal: signal
                ? AbortSignal.any([signal, AbortSignal.timeout(20000)])
                : AbortSignal.timeout(20000),
            },
          );
          for (const ev of events) {
            byId.set(ev.id, ev);
            unverifiedPinnedIds.delete(ev.id); // real copy wins
          }
        } catch {
          /* fall through to embedded fallbacks */
        }
      }

      // 2) Embedded fallbacks for anything relays didn't return.
      for (const fb of PINNED_FALLBACKS) {
        const ev = fb.event;
        if (byId.has(ev.id)) continue;
        if (fb.verified) {
          try {
            if (verifyEvent(ev)) {
              byId.set(ev.id, ev);
              unverifiedPinnedIds.delete(ev.id);
            }
          } catch {
            /* ignore */
          }
        } else if (ev.pubkey === PINNED_LOOKUP_AUTHOR) {
          byId.set(ev.id, ev);
          unverifiedPinnedIds.add(ev.id);
        }
      }

      return Array.from(byId.values());
    },
    enabled: PINNED_NOTE_IDS.length > 0 || PINNED_FALLBACKS.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
}
